import { useMemo, useState } from 'react'
import { Card, ErrorBox, Loading, Empty } from './ui'
import { money, num, pct, negClass, isNum, safeDiv } from './format'

const COLS = [
  ['name', 'Client', 'text'],
  ['orders', 'Orders', 'count'],
  ['revenue', 'Revenue', 'money'],
  ['direct_costs', 'Carrier & direct costs', 'money'],
  ['gross_profit', 'Gross profit', 'money'],
  ['cost_share', 'Share of daily costs', 'money'],
  ['profit', 'Profit', 'money'],
  ['profit_per_order', 'Profit per order', 'money2'],
  ['margin', 'Margin', 'pct'],
]

function cell(kind, v) {
  if (kind === 'count') return num(v)
  if (kind === 'money2') return money(v, 2)
  if (kind === 'pct') return pct(v)
  if (kind === 'money') return money(v)
  return v ?? '—'
}

// Unknowns always sort last, whichever direction.
function compare(a, b, key, dir) {
  const va = a[key]
  const vb = b[key]
  if (key === 'name') return String(va || '').localeCompare(String(vb || '')) * dir
  const na = isNum(va)
  const nb = isNum(vb)
  if (!na && !nb) return 0
  if (!na) return 1
  if (!nb) return -1
  return (Number(va) - Number(vb)) * dir
}

export default function ClientsTab({ query, periodLabel }) {
  const [sortKey, setSortKey] = useState('profit')
  const [sortDir, setSortDir] = useState(-1)
  const { data, error, isLoading } = query
  const clients = data?.clients
  const rows = useMemo(
    () => (clients || []).map((c) => ({ ...c, name: c.name || c.client_id })).sort((a, b) => compare(a, b, sortKey, sortDir)),
    [clients, sortKey, sortDir],
  )

  if (error) return <ErrorBox error={error} what="client profit" />
  if (isLoading && !data) return <Card title={`Profit by client, ${periodLabel}`}><Loading /></Card>
  if (!rows.length) return <Card title={`Profit by client, ${periodLabel}`}><Empty>No client activity in this period.</Empty></Card>

  const t = data.totals || {}
  const clickSort = (k) => {
    if (k === sortKey) setSortDir(-sortDir)
    else { setSortKey(k); setSortDir(k === 'name' ? 1 : -1) }
  }
  const known = rows.filter((r) => isNum(r.profit))
  const maxAbs = Math.max(0, ...known.map((r) => Math.abs(Number(r.profit))))
  const byProfit = [...rows].sort((a, b) => compare(a, b, 'profit', -1))

  return (
    <>
      <Card
        title={`Profit by client, ${periodLabel}`}
        sub={<><b>Share of daily costs</b> = each day&apos;s wages and overheads × (this client&apos;s orders that day ÷ all orders that day), added up over the period. Click a column to sort.</>}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-300">
                {COLS.map(([k, label]) => (
                  <th
                    key={k}
                    onClick={() => clickSort(k)}
                    className={`${k === 'name' ? 'text-left' : 'text-right'} font-semibold text-xs text-slate-500 px-2.5 py-2 whitespace-nowrap cursor-pointer hover:text-blue-600 select-none`}
                  >
                    {label}{sortKey === k ? (sortDir < 0 ? ' ▾' : ' ▴') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.client_id} className="border-b border-slate-100">
                  {COLS.map(([k, , kind]) => (
                    <td
                      key={k}
                      className={[
                        k === 'name' ? 'text-left' : 'text-right',
                        'px-2.5 py-1.5 tabular-nums whitespace-nowrap',
                        ['profit', 'profit_per_order', 'margin', 'gross_profit'].includes(k) ? negClass(r[k]) : '',
                        k === 'profit' ? 'font-semibold' : '',
                      ].join(' ')}
                    >
                      {cell(kind, r[k])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 font-bold tabular-nums">
                <td className="text-left px-2.5 py-1.5">All clients</td>
                <td className="text-right px-2.5 py-1.5">{num(t.orders)}</td>
                <td className="text-right px-2.5 py-1.5">{money(t.revenue)}</td>
                <td className="text-right px-2.5 py-1.5">{money(t.direct_costs)}</td>
                <td className="text-right px-2.5 py-1.5">{money(t.gross_profit)}</td>
                <td className="text-right px-2.5 py-1.5">{money(t.cost_share)}</td>
                <td className={`text-right px-2.5 py-1.5 ${negClass(t.profit)}`}>{money(t.profit)}</td>
                <td className="text-right px-2.5 py-1.5">{money(isNum(t.profit_per_order) ? t.profit_per_order : safeDiv(t.profit, t.orders), 2)}</td>
                <td className="text-right px-2.5 py-1.5">{pct(isNum(t.margin) ? t.margin : safeDiv(t.profit, t.revenue))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        {isNum(data.unallocated_cost) && Number(data.unallocated_cost) !== 0 && (
          <p className="text-xs text-slate-500 mt-2">
            Not on any client: {money(data.unallocated_cost)} of wages and overheads on working days with no orders
            (counted in the company totals on the Statement).
          </p>
        )}
      </Card>

      <Card title="Who makes you money" sub="Profit after each client's share of daily costs, best to worst.">
        {byProfit.map((r) => {
          const v = isNum(r.profit) ? Number(r.profit) : null
          const w = v === null || !maxAbs ? 0 : (Math.abs(v) / maxAbs) * 50
          const left = v !== null && v < 0 ? 50 - w : 50
          return (
            <div key={r.client_id} className="grid grid-cols-[minmax(80px,140px)_1fr_80px] items-center gap-2 text-[12.5px] my-1">
              <span className="truncate" title={r.name}>{r.name}</span>
              <div className="h-3 relative bg-slate-50 rounded-sm">
                <div className="absolute -top-0.5 -bottom-0.5 w-px bg-slate-400" style={{ left: '50%' }} />
                {v !== null && (
                  <div
                    className="absolute top-0 h-3 rounded-sm"
                    style={{ left: `${left}%`, width: `${w}%`, background: v < 0 ? '#dc2626' : '#10b981' }}
                  />
                )}
              </div>
              <span className={`text-right tabular-nums ${negClass(v)}`}>{money(v)}</span>
            </div>
          )
        })}
      </Card>
    </>
  )
}
